import { BranchType } from "@/features/church/config/branches/types/branch.types"
import type { Currency } from "@ibada/shared-types/finance"
import { PARISH_BRANCH_SLUG } from "@/shared/lib/routing/reserved-slugs"
import {
  getActiveUserBranchAccesses,
  hasOrganizationScopeAccess,
  isSingleBranchWorkspaceAccess,
  type WorkspaceAccessRecord,
} from "@/features/auth/lib/workspace-user-access"

export type AccessibleBranch = {
  id: string
  name: string
  slug: string
  branchType: BranchType
  baseCurrency?: Currency
  timezone?: string
}

const isNonEmptyString = (value: string | null | undefined): value is string =>
  typeof value === "string" && value.trim().length > 0

/**
 * The branches a user may act in, for switchers, pickers and branch resolution.
 *
 * Two structure-dependent rules:
 *
 * - MULTI-branch: the parish/SYSTEM branch is listed under the `parish` slug and labelled "Parish". It is a real
 *   destination there — the organization console, with its own books and nav — and its provisioned name
 *   (`SYSTEM_BRANCH_<workspace>`) is plumbing that must never reach a reader.
 * - MONO: the parish is NOT LISTED AT ALL. A mono church's parish is invisible: never navigated to, never
 *   granted, never operated on, because everything belongs to its one operational branch. Listing it — even
 *   masked as "Parish" — is what let the owner drift onto a branch invited users could not reach, and then
 *   configure finance where their own treasurer found nothing.
 */
export function buildAccessibleBranches(
  workspaceAccess: WorkspaceAccessRecord | null | undefined,
): AccessibleBranch[] {
  if (!workspaceAccess) return []

  const hideParish = isSingleBranchWorkspaceAccess(workspaceAccess)
  const branches = new Map<string, AccessibleBranch>()

  for (const branchAccess of getActiveUserBranchAccesses(workspaceAccess)) {
    const branch = branchAccess.branch
    if (!isNonEmptyString(branch?.id) || !isNonEmptyString(branch?.name)) continue

    const isSystemBranch = branch.branchType === BranchType.System
    if (isSystemBranch && hideParish) continue

    const slug = isSystemBranch ? PARISH_BRANCH_SLUG : branch.slug
    if (!isNonEmptyString(slug)) continue

    branches.set(branch.id, {
      id: branch.id,
      name: isSystemBranch ? "Parish" : branch.name,
      slug,
      branchType: (branch.branchType as BranchType | undefined) ?? BranchType.Normal,
      // Carried through so `useBranchCurrency()` reports the configured currency rather than falling back to USD.
      baseCurrency: (branch.baseCurrency ?? undefined) as Currency | undefined,
      timezone: branch.timezone ?? undefined,
    })
  }

  /*
   * ORGANIZATION scope reaches the parish without a branch grant.
   *
   * A workspace-scoped role is granted with no branch condition (`ability.factory.ts`), so it authorizes every
   * branch including the SYSTEM one — but it creates no `UserBranchAccess` row, so the loop above never sees
   * the parish. A user holding every organization role therefore had no "Organization" entry in the switcher
   * and no way into the scope they govern. The server sends `workspace.systemBranch` for exactly these
   * callers; adding it here is what puts the door back.
   *
   * After the loop, and keyed by branch id, so an explicit parish grant already collected wins and is not
   * duplicated. Skipped for mono, whose parish is invisible and whose payload omits the field anyway.
   */
  const systemBranch = workspaceAccess.workspace?.systemBranch
  if (!hideParish && systemBranch?.id && !branches.has(systemBranch.id) && hasOrganizationScopeAccess(workspaceAccess)) {
    branches.set(systemBranch.id, {
      id: systemBranch.id,
      name: "Parish",
      slug: PARISH_BRANCH_SLUG,
      branchType: BranchType.System,
      timezone: systemBranch.timezone ?? undefined,
    })
  }

  /*
   * Mono workspaces have one invisible organization/parish plus one operational branch.
   *
   * A regular mono member may have NO explicit UserBranchAccess rows at all, because the workspace itself is
   * the branch experience. The server already resolves that correctly by injecting `workspace.operationalBranch`
   * into the access matrix. The client must mirror that rule or it collapses to "no branch access" after
   * bootstrap, which is how the mono `/no-access?reason=tenant-access` regression happened.
   */
  const operationalBranch = workspaceAccess.workspace?.operationalBranch
  if (
    hideParish &&
    operationalBranch?.id &&
    operationalBranch?.slug &&
    !branches.has(operationalBranch.id)
  ) {
    branches.set(operationalBranch.id, {
      id: operationalBranch.id,
      name: operationalBranch.name,
      slug: operationalBranch.slug,
      branchType: BranchType.Normal,
      timezone: operationalBranch.timezone ?? undefined,
    })
  }

  for (const department of workspaceAccess.departmentAccess ?? []) {
    if (!branches.has(department.branchId)) {
      branches.set(department.branchId, {
        id: department.branchId,
        name: department.branchName,
        slug: department.branchSlug,
        branchType: BranchType.Normal,
      })
    }
  }

  return [...branches.values()]
}
